import React from 'react';
import { observer } from 'mobx-react';
import { Button, CardImg, Col, Row } from 'reactstrap';
import CmsPackage from '../../Models/CMS/CmsPackage';
import CmsHotelData from '../../Models/CMS/CmsHotelData';

import Keys from '../../Translations/generated/deal-finder.en.json.keys';
import { useTranslation } from 'react-i18next';
import { QueryUtils } from '../../Util/QueryUtils';
import useQuery from '../../Util/useQuery';

interface DealFinderHotelPickerProps {
  packageModel?: CmsPackage;
  hotels: CmsHotelData[];
  selectedHotels: CmsHotelData[];
  onHotelSelect: (hotel: CmsHotelData) => void;
  onSelectAll: () => void;
  isMobile?: boolean;
  selectAllHotels?: boolean;
}

const DealFinderHotelPicker = observer(function DealFinderHotelPicker(
  props: DealFinderHotelPickerProps
) {
  const {
    packageModel,
    hotels,
    selectedHotels,
    onHotelSelect,
    isMobile,
    selectAllHotels,
    onSelectAll
  } = props;

  const { t } = useTranslation('deal-finder');
  const query = useQuery();
  const itemFromQuery = QueryUtils.getItem(query);

  function buildHotelRow(cmsHotel: CmsHotelData, index: number) {
    const isSelected =
      !!selectedHotels.find(value => value.typeCode === cmsHotel.typeCode) || selectAllHotels;

    return (
      <Button
        className={`mb-1 deal-finder__hotel-row_container ${isSelected ? 'selected_hotel' : ''}`}
        onClick={() => onHotelSelect(cmsHotel)}
        aria-label={cmsHotel.title ?? t(Keys.hotel)}
        role={'option'}
        key={'deal-finder-hotel_' + index + cmsHotel.typeCode}
      >
        <Row className={'p-1'}>
          {cmsHotel.image ? (
            <Col xs={4} className="align-content-center">
              <CardImg
                className="deal-finder__hotel-row_img"
                src={cmsHotel?.image?.small?.url || ''}
                alt={cmsHotel?.image?.imageAlt ?? 'Hotel Picture'}
                title={cmsHotel?.image?.imageCaption ?? 'Hotel Picture'}
                onClick={(): void => {}}
              />
            </Col>
          ) : (
            <></>
          )}
          <Col xs={8} className="d-flex flex-column justify-content-between">
            {cmsHotel.title ? (
              <Row>
                <Col className="font-weight-bolder">{cmsHotel.title}</Col>
              </Row>
            ) : (
              <> </>
            )}
            {cmsHotel.distanceDescription ? (
              <Row>
                <Col className="font-weight-bolder">{cmsHotel.distanceDescription || ''}</Col>
              </Row>
            ) : (
              <> </>
            )}
          </Col>
        </Row>
      </Button>
    );
  }

  function getHotelSectionLabel(): string {
    if (selectedHotels.length > 0) {
      if (selectedHotels.length == 1) {
        return selectedHotels[0].title
          ? selectedHotels[0].title
          : `1 ${t(Keys.selectedHotelsLabel)}`; //NOSONAR
      } else {
        return `${selectedHotels.length} ${t(Keys.selectedHotelsLabel)}`;
      }
    }

    return t(Keys.hotel);
  }

  return (
    <Row className={`${isMobile ? '' : 'meg_deal-finder-hotel-selector_container h-100'} px-1`}>
      <Col>
        <Row className="pb-2">
          {isMobile ? (
            <></>
          ) : (
            <Col xs={8} className="font-weight-bold pl-0">
              {getHotelSectionLabel()}
            </Col>
          )}
          {packageModel?.dealFinderConfig?.showPreselectAll && !itemFromQuery ? (
            <Col
              xs={isMobile ? 12 : 4}
              className={`meg_deal-finder_checkbox pr-0 ${isMobile ? 'pl-0' : ''}`}
            >
              <label aria-label={t(Keys.preselectAllHotelsCheckbox)}>
                {t(Keys.selectAll)}
                <input
                  className="meg_deal-finder_checkbox"
                  type="checkbox"
                  onChange={() => {
                    onSelectAll();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onSelectAll();
                    }
                  }}
                  checked={!!selectAllHotels}
                />
              </label>
            </Col>
          ) : (
            <> </>
          )}
        </Row>

        <Row className={`${isMobile ? 'meg_deal-finder-hotel-selector_container-mobile ' : ''}`}>
          <Col
            className={`pt-2 ${isMobile ? '' : 'scrollableList'}`}
            aria-label={t(Keys.listOfHotels)}
          >
            {hotels.map((cmsHotel, index) => buildHotelRow(cmsHotel, index))}
          </Col>
        </Row>
      </Col>
    </Row>
  );
});

export default DealFinderHotelPicker;
